import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase/client';
import { trackSupabaseError } from '@/lib/analytics/errorHandler';

/**
 * Whether the signed-in client's case is firm-managed — i.e. an active advisor
 * connection exists (#1322). Firm-managed clients get attorney-oriented copy on
 * the stepper ("your attorney needs X") instead of DIY strategy framing.
 */
export function useFirmManagedCase(): { isFirmManaged: boolean; isLoading: boolean } {
  const { user } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ['firm-managed-case', user?.id],
    queryFn: async (): Promise<boolean> => {
      const { count, error } = await supabase
        .from('advisor_connections')
        .select('id', { count: 'exact', head: true })
        .eq('client_user_id', user!.id)
        .eq('status', 'active');

      if (error) {
        trackSupabaseError('select', 'advisor_connections', error);
        throw error;
      }
      return (count ?? 0) > 0;
    },
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 5,
  });

  return { isFirmManaged: !!data, isLoading };
}
